import api from 'api/axiosInstance';
import type { MonthlyPaymentVolume } from 'types/settings.types';
import type {
  BirthInfo,
  BusinessAddress,
} from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboarding.types';
import type { SwanOnboardingStepIdType } from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboardingStepIds';
import type { CountryAlpha3Code } from 'types/countries';
import type { AvailableCountries } from 'types/users.types';

const ONBOARDING_VERSION = 2;

export type AccountableOnboardingBody = {
  version?: number;
  country?: AvailableCountries;
  userId?: string;
  _id?: string;
  filepath?: string;
  filepaths?: string[];
  taxID?: string;
  skipDocumentScan?: boolean;
  furthestStepCompleted?: SwanOnboardingStepIdType;
  personalDetails?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    address?: {
      city?: string;
      state?: string;
      street?: string;
      zip?: string;
      country?: CountryAlpha3Code;
    };
  };
  businessAddress?: BusinessAddress;
  birth?: BirthInfo;
  webLink?: string;
  businessActivity?: string;
  businessActivityDescription?: string;
  monthlyPaymentVolume?: MonthlyPaymentVolume;
  companyName?: string;
  additionalBusinessActivityDescription?: string;
};

export type AccountableOnboardingResponse = {
  businessActivity?: string;
  businessActivityDescription?: string;
  companyName: string;
  country: AvailableCountries;
  created: string;
  filepaths: string[];
  furthestStepCompleted: SwanOnboardingStepIdType;
  modified: string;
  monthlyPaymentVolume: MonthlyPaymentVolume;
  onboardingId: string;
  personalDetails: {
    firstName: string;
    lastName: string;
    email: string;
    address: {
      city: string;
      state: string;
      street: string;
      zip: string;
    };
  };
  businessAddress?: BusinessAddress;
  birth?: BirthInfo;
  webLink?: string;
  version?: number;
  reminders: { kycVerification: { lastSentAt: string } };
  status: string;
  supportingDocumentCollectionId: string;
  taxID: string;
  userId: string;
  __v: number;
  _id: string;
  wholeDocumentIssues: WholeDocumentIssuesEnum[];
  documentIssues?: {
    filepath: string;
    issues: DocumentIssuesEnum[];
  }[];
};

export enum WholeDocumentIssuesEnum {
  INVALID_DOCUMENT_TYPE = 'invalid_document_type',
  MISSING_PAGES = 'missing_pages',
  MISSING_STAMP_OR_SIGNATURE = 'missing_stamp_or_signature',
  MISSING_DATE = 'missing_date',
  MISSING_ACTIVITY = 'missing_activity',
}

export enum DocumentIssuesEnum {
  PAGE_IS_BLURRY = 'page_is_blurry',
  CORNERS_CUT_OFF = 'corners_cut_off',
  CONTAINS_BLACKED_OUT_FIELDS = 'contains_blacked_out_fields',
}

const patchAccountableBankingOnboarding = (
  body: AccountableOnboardingBody,
): Promise<AccountableOnboardingResponse> =>
  api.patch<unknown, AccountableOnboardingResponse>(
    '/v2/accountable-banking/onboarding',
    { ...body, version: ONBOARDING_VERSION },
  );

export default patchAccountableBankingOnboarding;
